<template>
  <div>
    <b-modal
      v-model="showGallery"
      id="gallery-modal"
      size="lg"
      scrollable
      :title="propertyData?.property?.name"
    >
      <div class="mt-5 p-3">
        <div id="gallery" class="row">
          <a
            v-for="image in propertyImages"
            :key="image.id"
            :href="image.url"
            :data-pswp-width="image.width"
            :data-pswp-height="image.height"
            target="_blank"
            rel="noreferrer"
            class="col-md-3 col-6 p-0 p-1"
          >
            <img :src="image.url" class="gallery-image" alt="" />
          </a>
          <template v-if="isLoading">
            <div class="col-md-3 col-6 p-0 p-1"  v-for="i in 8" :key="i">
              <b-skeleton height="200px"></b-skeleton>
            </div>
          </template>
          <InfiniteLoading
            ref="infiniteLoading"
            @infinite="infiniteHandler"
            :identifier="infiniteId"
          >
            <div slot="spinner"></div>
            <div slot="no-results"></div>
            <div slot="no-more"></div>
          </InfiniteLoading>
        </div>
      </div>
      <template #modal-footer="{ cancel }">
        <button class="btn g-primary-button" @click="cancel()">
          {{ $t("property.closeGallery") }}
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import InfiniteLoading from "vue-infinite-loading";
import PhotoSwipeLightbox from "photoswipe/dist/photoswipe-lightbox.esm.js";
import "photoswipe/dist/photoswipe.css";
export default {
  name: "MediaGallery",
  props: {
    propertyId: {
      type: Number,
      required: true,
    },
    defaultMedia: {
      type: Array,
      required: false,
    },
  },
  components: {
    InfiniteLoading,
  },
  data() {
    return {
      currentPage: 1,
      lightbox: null,
      showGallery: false,
      propertyImages: [],
      isLoading: false,
      noMoreData: false,
      infiniteId: +new Date(),
    };
  },
  computed: {
    ...mapState("property", ["media", "propertyData"]),
  },
  methods: {
    ...mapActions("property", ["fetchPropertyMedia"]),
    loadImageSize(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = () => {
          reject(new Error(`Image failed to load: ${url}`));
        };
        img.src = url;
      });
    },
    async addDimensionData(img) {
      try {
        const size = await this.loadImageSize(img.url);
        if (size.width && size.height) {
          img.width = size.width;
          img.height = size.height;
        }
      } catch (e) {
        console.warn("Failed to load image size for:", img.url, e);
      }
      return img;
    },
    show() {
      this.showGallery = true;

      setTimeout(() => {
        if (this.lightbox) {
          this.lightbox.destroy();
          this.lightbox = null;
        }

        this.lightbox = new PhotoSwipeLightbox({
          gallery: "#gallery",
          children: "a",

          initialZoomLevel: 1,
          secondaryZoomLevel: 1.5,

          pswpModule: () => import("photoswipe"),
        });

        this.lightbox.init();
      }, 1000);
    },
    resetList() {
      this.currentPage = 1;
      this.propertyImages = [];
      this.infiniteId += 1;
    },
    async infiniteHandler($state) {
      if (this.isLoading || this.noMoreData) {
        $state.complete();
        return;
      }

      this.isLoading = true;

      try {
        const data = await this.fetchPropertyMedia({
          propertyId: this.propertyId,
          params: { page: this.currentPage, perPage: 15 },
        });

        if (!data?.content?.length) {
          this.noMoreData = true;
          $state.complete();
        } else {
          const images = await Promise.all(
            data.content.map(async (img) => await this.addDimensionData(img))
          );

          this.propertyImages.push(...images);
          this.currentPage++;
          $state.loaded();
        }
      } catch (error) {
        console.error("Error fetching items:", error);
        $state.error();
      } finally {
        this.isLoading = false;
      }
    },
  },
  async mounted() {
    const images = await Promise.all(
      this.defaultMedia.map(async (img) => await this.addDimensionData(img))
    );

    this.propertyImages.push(...images);
    this.currentPage = 2;
  },
  unmounted() {
    if (this.lightbox) {
      this.lightbox.destroy();
      this.lightbox = null;
    }
  },
};
</script>

<style>
#gallery-modal .modal-header {
  background: #fff;
}

#gallery-modal .modal-dialog {
  max-width: 100vw;
}

.gallery {
  max-height: 800px;
  overflow-y: auto;
}

.gallery-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 10px;
}

@media screen and (max-width: 767px) {
  .gallery-image {
    height: 100px;
    border-radius: 5px;
  }
}
</style>
